import { markRaw } from 'vue'
import moment from "moment/moment";

const cache = {
  items: {},
  suppliers: {},
  sales: {},
  itemWeeklySales: {},
  weeklySalesCalculated: false,
  hiddenInventory: null
}

export function setLicenseItems(licenseId, items) {
  cache.items[licenseId] = markRaw(items); // no reactivity overhead
}

export function getLicenseItems(licenseId) {
  return cache.items[licenseId];
}

export function hasLicenseItems(licenseId) {
  return typeof cache.items[licenseId] !== 'undefined';
}

export function setLicenseSuppliers(licenseId, suppliers) {
  cache.suppliers[licenseId] = markRaw(suppliers); // no reactivity overhead
}

export function getLicenseSuppliers(licenseId) {
  return cache.suppliers[licenseId];
}

export function hasLicenseSuppliers(licenseId) {
  return typeof cache.suppliers[licenseId] !== 'undefined';
}

export function setLicenseSales(licenseId, sales) {
  cache.sales[licenseId] = markRaw(sales); // no reactivity overhead
}

export function getLicenseSales(licenseId) {
  return cache.sales[licenseId];
}

export function hasLicenseSales(licenseId) {
  return typeof cache.sales[licenseId] !== 'undefined';
}

export function clearInventoryReorderCache() {
  cache.items = {};
  cache.suppliers = {};
  cache.sales = {};
  cache.itemWeeklySales = {};
  cache.weeklySalesCalculated = false;
  cache.hiddenInventory = null;
}

export function hasHiddenInventory() {
  return cache.hiddenInventory !== null;
}

export function setHiddenInventory(metrcItems, products) {
  cache.hiddenInventory = {
    metrc_items: metrcItems,
    products: products
  }
}

export function getHiddenMetrcItems() {
  return cache.hiddenInventory?.metrc_items || [];
}

export function getHiddenProducts() {
  return cache.hiddenInventory?.products || [];
}

export function isWeeklySalesCalculated() {
  return !! cache.weeklySalesCalculated;
}

export function calculateItemsWeeklySales() {
  let itemWeeklySales = {},
    momentWeek0Start = moment().startOf("week"),
    momentWeek0End = moment().endOf("week"),
    momentWeek1Start = moment().subtract(1, "week").startOf("week"),
    momentWeek1End = moment().subtract(1, "week").endOf("week"),
    momentWeek2Start = moment().subtract(2, "week").startOf("week"),
    momentWeek2End = moment().subtract(2, "week").endOf("week"),
    momentWeek3Start = moment().subtract(3, "week").startOf("week"),
    momentWeek3End = moment().subtract(3, "week").endOf("week"),
    momentWeek4Start = moment().subtract(4, "week").startOf("week"),
    momentWeek4End = moment().subtract(4, "week").endOf("week");

  for (let [licenseId, sales] of Object.entries(cache.sales)) {
    for (let i = 0; i < sales.length; i++) {
      const sale = sales[i];
      const itemId = sale.metrc_item_id;
      if (typeof itemWeeklySales[itemId] === 'undefined') {
        itemWeeklySales[itemId] = {};
      }
      if (typeof itemWeeklySales[itemId][licenseId] === 'undefined') {
        itemWeeklySales[itemId][licenseId] = {
          week_0_sales: 0,
          week_1_sales: 0,
          week_2_sales: 0,
          week_3_sales: 0,
          week_4_sales: 0,
        };
      }

      const saleDateMoment = moment(sale.date);
      const saleQty = Math.round((sale.inventory_value + Number.EPSILON) * 100) / 100;

      if (
        saleDateMoment.isSameOrAfter(momentWeek0Start) &&
        saleDateMoment.isSameOrBefore(momentWeek0End)
      ) {
        itemWeeklySales[itemId][licenseId].week_0_sales += saleQty;
      } else if (
        saleDateMoment.isSameOrAfter(momentWeek1Start) &&
        saleDateMoment.isSameOrBefore(momentWeek1End)
      ) {
        itemWeeklySales[itemId][licenseId].week_1_sales += saleQty;
      } else if (
        saleDateMoment.isSameOrAfter(momentWeek2Start) &&
        saleDateMoment.isSameOrBefore(momentWeek2End)
      ) {
        itemWeeklySales[itemId][licenseId].week_2_sales += saleQty;
      } else if (
        saleDateMoment.isSameOrAfter(momentWeek3Start) &&
        saleDateMoment.isSameOrBefore(momentWeek3End)
      ) {
        itemWeeklySales[itemId][licenseId].week_3_sales += saleQty;
      } else if (
        saleDateMoment.isSameOrAfter(momentWeek4Start) &&
        saleDateMoment.isSameOrBefore(momentWeek4End)
      ) {
        itemWeeklySales[itemId][licenseId].week_4_sales += saleQty;
      }
    }
  }

  for (let [itemId, licenseSales] of Object.entries(itemWeeklySales)) {
    for (let [licenseId, licenseWeekly] of Object.entries(licenseSales)) {
      itemWeeklySales[itemId][licenseId] = {
        week_0_sales: Math.round((itemWeeklySales[itemId][licenseId].week_0_sales + Number.EPSILON) * 100) / 100,
        week_1_sales: Math.round((itemWeeklySales[itemId][licenseId].week_1_sales + Number.EPSILON) * 100) / 100,
        week_2_sales: Math.round((itemWeeklySales[itemId][licenseId].week_2_sales + Number.EPSILON) * 100) / 100,
        week_3_sales: Math.round((itemWeeklySales[itemId][licenseId].week_3_sales + Number.EPSILON) * 100) / 100,
        week_4_sales: Math.round((itemWeeklySales[itemId][licenseId].week_4_sales + Number.EPSILON) * 100) / 100,
      };
    }
  }

  cache.itemWeeklySales = markRaw(itemWeeklySales);

  cache.weeklySalesCalculated = true;
}

export function getItemWeeklySales(itemId, licenseId = null) {
  const weeklySales = cache.itemWeeklySales[itemId];
  if (licenseId) {
    if (weeklySales && typeof weeklySales[licenseId] !== 'undefined') {
      return weeklySales[licenseId];
    } else {
      return {
        week_0_sales: 0,
        week_1_sales: 0,
        week_2_sales: 0,
        week_3_sales: 0,
        week_4_sales: 0,
      };
    }
  }

  return weeklySales;
}
